// ── THE GRANT COMMAND — validated input, literal substitution (SCR item 3) ─────────────────────
//
// ⚠ THE PAGE HANDS AN ADMIN A COMMAND TO RUN WITH SHAREPOINT-ADMIN RIGHTS, so what it builds must be
//   exactly the library address and nothing else. The address is typed (or pasted) by a person and
//   lands inside a single-quoted PowerShell argument: a ' — or one of PowerShell's CURLY single
//   quotes (U+2018–U+201B), which it also treats as quotes — would end the argument and let the rest
//   of the input run as code in the admin's session. So the address is VALIDATED before any command
//   exists, and on refusal there is no command and nothing to copy.
//
// ⚠ AND THE SUBSTITUTION IS LITERAL. String.replace with a STRING replacement interprets `$&`, `$'`,
//   `` $` `` and `$$`; with a FUNCTION replacement it does not. Validation already refuses `$`, so the
//   replacer is the second layer — each is proven on its own.
//
// ⚠ NOT OVER-TIGHT. A real library address — as a browser copies it, %-encoded (VeriGRC%20Evidence)
//   — passes. Only the dangerous LITERALS and control characters are refused; an encoded sequence is
//   never refused for being encoded.

/** The literals that are never allowed, named so a reader (and a test) can see each one. */
export const FORBIDDEN_IN_ADDRESS: ReadonlyArray<string> = [
  "'",        // U+0027 — ends PowerShell's single-quoted argument
  '‘', '’', '‚', '‛',   // curly single quotes — PowerShell treats them as quotes too
  '$',        // PowerShell variable / JS replace pattern
  ';',        // statement separator
  '`',        // PowerShell escape / continuation
];

/**
 * The URL-safe characters an address may contain: RFC 3986 unreserved + the reserved delimiters a
 * library URL uses, and `%` (only as a valid %XX escape — checked below). Deliberately WITHOUT `'`,
 * `$` and `;`, and without spaces, quotes, backslash, braces, pipes and every non-ASCII character.
 */
const URL_SAFE = /^[A-Za-z0-9\-._~:/?#[\]@!&()*+,=%]+$/;
// eslint-disable-next-line no-control-regex
const CONTROL = /[\u0000-\u001F\u007F-\u009F]/;
const BAD_ESCAPE = /%(?![0-9A-Fa-f]{2})/;

export type AddressCheck = { ok: true } | { ok: false; reason: string };

export const NOT_A_LIBRARY_ADDRESS =
  'That is not a library address. Paste the address exactly as your browser shows it — for example '
  + 'https://contoso.sharepoint.com/sites/Compliance/VeriGRC%20Evidence.';

/**
 * Is this a SharePoint document-library address the grant script can take?
 *
 * ⓘ `/sites/<site>/<library>` mirrors the script's own requirement (it refuses anything else), so
 *   the page never builds a command the script would reject.
 */
export function checkLibraryAddress(input: string): AddressCheck {
  const refuse = { ok: false as const, reason: NOT_A_LIBRARY_ADDRESS };
  if (!input) return refuse;
  if (FORBIDDEN_IN_ADDRESS.some((c) => input.includes(c))) return refuse;
  if (CONTROL.test(input)) return refuse;
  if (!URL_SAFE.test(input) || BAD_ESCAPE.test(input)) return refuse;
  let url: URL;
  try { url = new URL(input); } catch { return refuse; }
  if (url.protocol !== 'https:') return refuse;
  if (!/^[a-z0-9-]+\.sharepoint\.com$/i.test(url.hostname) || url.port !== '' || url.username || url.password) {
    return refuse;
  }
  const segments = url.pathname.split('/').filter(Boolean);
  if (segments.length < 3 || segments[0].toLowerCase() !== 'sites') return refuse;
  return { ok: true };
}

/**
 * ⚠ THE APP-ID GUARD (cmd.exe fix, MI) — separate from the address validator. The application ID lands
 * inside a DOUBLE-quoted argument, so it must be exactly a canonical GUID: 8-4-4-4-12 hex, either case.
 * The shape alone, deliberately — no UUID version or variant rule (MI), so any well-formed application
 * ID is accepted, while a straight `"`, a curly double quote or anything else that is not hex-and-hyphen
 * leaves no command at all.
 */
const CANONICAL_APP_ID = /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/;
export function isCanonicalAppId(appId: string): boolean {
  return CANONICAL_APP_ID.test(appId);
}

/**
 * The admin's command: the server's template with the two values substituted LITERALLY.
 * Returns null unless the address passes `checkLibraryAddress`. The app id is server-supplied, but it
 * lands in the same quoted context, so it gets the same literal and control-character refusal.
 */
export function buildGrantCommand(template: string, libraryUrl: string, appId: string | null): string | null {
  if (!appId || FORBIDDEN_IN_ADDRESS.some((c) => appId.includes(c)) || CONTROL.test(appId)) return null;
  if (!isCanonicalAppId(appId)) return null;
  if (!checkLibraryAddress(libraryUrl).ok) return null;
  return substituteLiterally(template, libraryUrl, appId);
}

/** ⚠ FUNCTION replacements — `$&`, `$'`, `` $` `` and `$$` in a value are inserted as written. */
export function substituteLiterally(template: string, libraryUrl: string, appId: string): string {
  return template
    .replace('{{LIBRARY_URL}}', () => libraryUrl)
    .replace('{{APP_ID}}', () => appId);
}
